import { useEffect, useRef, useState } from "react";
import localeData from "dayjs/plugin/localeData";
import timezone from "dayjs/plugin/timezone";
import { Callout, DirectionalHint } from "@fluentui/react/lib/Callout";
import { edcDateFormat } from "maven-lib/dist/consts/edc/lib";
import { classNames } from "maven-lib/dist/utils/misc";
import { CrCalendarExt } from "@maven-surface/components/base/CrCalendarExt";
import { CrInput, CrInputProps } from "@maven-surface/components/base/CrInput";
import dayjs from "dayjs";

dayjs.extend(timezone);

export interface CrCalendarInputProps extends Omit<CrInputProps, "defaultValue" | "value" | "iconName"> {
  format: "YYYY/MM/DD" | "YYYY-MM-DD" | "DD-MMM-YYYY" | "DD/MMM/YYYY";
  typeable?: boolean;
  minDate?: Date | number | "TODAY";
  maxDate: Date | number | "TODAY";
  allUnknownable: boolean;
  yearUnknownable: boolean;
  monthUnknownable: boolean;
  monthDayUnknownable: boolean;
  dayUnknownable: boolean;
  defaultValue?: string | Date;
  value?: string | Date;
  onDeleteDate?: () => void;
}

export function CrCalendarInput(props: CrCalendarInputProps) {
  const calendarInputRef = useRef<HTMLDivElement>(null);

  const {
    maxDate,
    minDate,
    format,
    defaultValue: defaultValueO,
    value: valueO,
    onValueChange,
    readonly,
    allUnknownable,
    yearUnknownable,
    monthUnknownable,
    monthDayUnknownable,
    dayUnknownable,
    onDeleteDate,
    disabled,
    typeable,
    ...extras
  } = props;

  dayjs.extend(localeData);

  const controlledValueMapper = (defaultValue: string | Date, value: string | Date) => {
    const removeUnneedChar = (val: string) => val.split("/").join("").split("-").join("");

    if (typeof value === "string") return edcDateFormat(removeUnneedChar(value), format);
    else if (value instanceof Date) return dayjs(value).tz(undefined).format(format);
    else if (typeof defaultValue === "string") return edcDateFormat(removeUnneedChar(defaultValue), format);
    else if (defaultValue instanceof Date) return dayjs(defaultValue).tz(undefined).format(format);
    else return "";
  };

  const initialControlledValue = controlledValueMapper(defaultValueO!, valueO!);
  const [showCalendar, setShowCalendar] = useState(false);
  const [controlledValue, setControlledValue] = useState<string>(initialControlledValue);

  useEffect(() => {
    setControlledValue(initialControlledValue);
  }, [initialControlledValue]);

  // Note: 첫 렌더링 시에만 타임존이 적용되어야 하고 후에는 선택한 value 값 그대로 바인드되어야 함. 그러지 않을 경우 선택한 값에서 timezone이 적용되기 때문에 타임존의 offset에 따라 날짜가 변경될 수 있음
  useEffect(() => {
    setControlledValue(controlledValueMapper(defaultValueO!, valueO!));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissCalendarCallout = () => {
    setShowCalendar(false);
  };

  return (
    <div className={classNames("cr-calendar-input", showCalendar && "is-active")}>
      <div ref={calendarInputRef}>
        <CrInput
          value={controlledValue}
          disabled={disabled}
          onClick={() => !readonly && setShowCalendar(true)}
          readonly={true}
          iconName="Calendar"
          {...extras}
        />
      </div>
      {showCalendar && (
        <Callout
          isBeakVisible={true}
          className="ms-DatePicker-callout cr-calendar-callout"
          gapSpace={0}
          target={calendarInputRef.current?.querySelector(".ms-TextField-fieldGroup")}
          directionalHint={DirectionalHint.bottomCenter}
          preventDismissOnEvent={(event) => {
            if (typeable) {
              // Mobile 환경에서 키보드로 인한 이벤트만 dismiss 방지
              // iOS: scroll, Android: resize
              const allowedEvents = ["scroll", "resize", "focus", "focusin"];

              if (allowedEvents.includes(event.type)) return true;
            }

            return false;
          }}
          onDismiss={dismissCalendarCallout}
        >
          <CrCalendarExt
            maxDate={maxDate}
            minDate={minDate}
            typeable={typeable!}
            selectedDate={
              controlledValue
                ? format === "DD-MMM-YYYY" || format === "DD/MMM/YYYY"
                  ? {
                      year:
                        controlledValue.length === 10
                          ? parseInt(controlledValue.slice(6, 10), 10) || undefined
                          : parseInt(controlledValue.slice(7, 11), 10) || undefined,
                      month:
                        controlledValue.slice(3, 6) === "UNK"
                          ? undefined
                          : parseInt(
                              (
                                dayjs
                                  .localeData()
                                  .monthsShort()
                                  .findIndex((name) => name === controlledValue.slice(3, 6)) + 1
                              ).toString(),
                              10
                            ),
                      day: parseInt(controlledValue.slice(0, 2), 10) || undefined,
                    }
                  : {
                      year: parseInt(controlledValue.slice(0, 4), 10) || undefined,
                      month: parseInt(controlledValue.slice(5, 7), 10) || undefined,
                      day: parseInt(controlledValue.slice(8, 10), 10) || undefined,
                    }
                : undefined
            }
            allUnknownable={allUnknownable}
            yearUnknownable={yearUnknownable}
            monthUnknownable={monthUnknownable}
            monthDayUnknownable={monthDayUnknownable}
            dayUnknownable={dayUnknownable}
            format={format}
            onDismiss={dismissCalendarCallout}
            onValueChange={(vv) => {
              const v = (() => {
                if (format === "YYYY/MM/DD") return `${vv.slice(0, 4)}/${vv.slice(4, 6)}/${vv.slice(6, 8)}`;
                else if (format === "YYYY-MM-DD") return `${vv.slice(0, 4)}-${vv.slice(4, 6)}-${vv.slice(6, 8)}`;
                else if (format === "DD-MMM-YYYY") {
                  return vv.length === 9
                    ? `${vv.slice(0, 2)}-${vv.slice(2, 5)}-${vv.slice(5, 9)}`
                    : `${vv.slice(6, 8)}-${
                        vv.slice(4, 6) === "UK"
                          ? "UNK"
                          : dayjs()
                              .month(Number(vv.slice(4, 6)) - 1)
                              .format("MMM")
                      }-${vv.slice(0, 4)}`;
                } else if (format === "DD/MMM/YYYY") {
                  return vv.length === 9
                    ? `${vv.slice(0, 2)}/${vv.slice(2, 5)}/${vv.slice(5, 9)}`
                    : `${vv.slice(6, 8)}/${
                        vv.slice(4, 6) === "UK"
                          ? "UNK"
                          : dayjs()
                              .month(Number(vv.slice(4, 6)) - 1)
                              .format("MMM")
                      }/${vv.slice(0, 4)}`;
                }

                // NOTE: 다른 format 추가됐을 때 대비해서 남겨 둠
                return `${vv.slice(0, 4)}/${vv.slice(4, 6)}/${vv.slice(6, 8)}`;
              })();

              onValueChange && onValueChange(v);
              setControlledValue(v);
              dismissCalendarCallout();
            }}
            onDeleteDate={() => {
              onDeleteDate && onDeleteDate();
              setControlledValue("");
              dismissCalendarCallout();
            }}
          />
        </Callout>
      )}
    </div>
  );
}

CrCalendarInput.defaultProps = {
  allUnknownable: false,
  yearUnknownable: false,
  monthUnknownable: false,
  monthDayUnknownable: false,
  dayUnknownable: false,
  maxDate: "TODAY",
};
